/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file 用户行为监控插件
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */
import { __assign, __values } from "tslib";
import { fill } from '../utils/fill';
import { formatBehaviorData, parseUrl, transformResponse } from '../utils/misc';
import { wrap } from '../utils/wrapper';
import { isFunction, isString } from '../utils/is';
import { getGlobal } from '../utils/global';
import { supportsDocument, supportsFetch, supportsHistory } from '../utils/supports';
import { breadcrumbEventHandler, keypressEventHandler } from './helper';
var global = getGlobal();
var lastHref;
var MAX_RES_LENGTH = 8192;
var REDIRECT_CATCH_STATUS = [301, 302, 304];
/**
 * 行为数据插件
 */
var BehaviorPlugin = /** @class */ (function () {
    function BehaviorPlugin(options) {
        this.name = BehaviorPlugin.id;
        this.options = __assign({ beacon: true, console: true, dom: true, fetch: true, history: true, weirwood: true, xhr: true }, options);
    }
    /**
     * 安装插件
     * @param client
     */
    BehaviorPlugin.prototype.setupOnce = function (client) {
        var e_1, _a;
        this.client = client;
        var keys = Object.keys(this.options);
        try {
            for (var keys_1 = __values(keys), keys_1_1 = keys_1.next(); !keys_1_1.done; keys_1_1 = keys_1.next()) {
                var k = keys_1_1.value;
                var funcName = 'instrument' + k[0].toUpperCase() + k.slice(1);
                if (this.options[k] && this[funcName] && typeof this[funcName] === 'function') {
                    this[funcName].call(this);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (keys_1_1 && !keys_1_1.done && (_a = keys_1.return)) _a.call(keys_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    };
    /**
     * 监听 DOM 事件
     */
    BehaviorPlugin.prototype.instrumentDom = function () {
        if (!supportsDocument()) {
            return;
        }
        var d = global.document;
        d.addEventListener('click', breadcrumbEventHandler.call(this, this.client, 'click'), false);
        d.addEventListener('keypress', keypressEventHandler.call(this, this.client), false);
    };
    /**
     * 监听 XHR 网络请求事件
     */
    BehaviorPlugin.prototype.instrumentXhr = function () {
        var client = this.client;
        var _a = client.options, collectAutoAPIException = _a.collectAutoAPIException, isApiException = _a.isApiException;
        if (!('XMLHttpRequest' in global)) {
            return;
        }
        /** JSDoc */
        function wrapProp(prop, xhr) {
            // TODO: Fix XHR types
            if (prop in xhr && isFunction(xhr[prop])) {
                fill(xhr, prop, function (original) {
                    return wrap({
                        mechanism: {
                            data: {
                                function: prop,
                                handler: (original && original.name) || '<anonymous>'
                            },
                            handled: true,
                            type: 'instrument'
                        }
                    }, original);
                });
            }
        }
        var xhrproto = XMLHttpRequest.prototype;
        var globalKey = client._globalKey || '';
        // hack xhr open 方法
        fill(xhrproto, 'open', function (origOpen) {
            return function (method, url) {
                // preserve arity
                // if Sentry key appears in URL, don't capture
                if (isString(url) && url.indexOf(globalKey) === -1) {
                    // 记录 __raven_xhr 数据
                    this.__raven_xhr = {
                        method: method,
                        url: url,
                        status_code: null,
                    };
                }
                return origOpen.apply(this, arguments);
            };
        }, client._wrappedBuiltIns);
        fill(xhrproto, 'abort', function (origAbort) {
            return function () {
                var xhr = this;
                if (xhr.__raven_xhr) {
                    xhr.__raven_xhr.abortedFlag = true;
                }
                return origAbort.apply(this, arguments);
            };
        }, client._wrappedBuiltIns);
        // hack xhr send 方法
        fill(xhrproto, 'send', function (origSend) {
            return function () {
                var xhr = this;
                var sendBody = arguments;
                // 自定义的回调处理函数
                function onreadystatechangeHandler() {
                    if (!xhr.__raven_xhr || xhr.readyState !== 4) {
                        return;
                    }
                    try {
                        // touching statusCode in some platforms throws
                        // an exception
                        xhr.__raven_xhr.status_code = xhr.status;
                    }
                    catch (e) {
                        /* do nothing */
                    }
                    // 对response进行判断和处理生成isApiException需要的信息
                    var res = transformResponse(xhr.responseType, xhr.response);
                    // 发送前，捕获行为
                    client.captureBehavior({
                        type: 'http',
                        category: 'xhr',
                        data: __assign(__assign({}, xhr.__raven_xhr), { response: formatBehaviorData(res), params: formatBehaviorData((sendBody && sendBody[0]) ? sendBody[0] : '') }),
                    });
                    // 是否自动上报
                    if (!collectAutoAPIException) {
                        return;
                    }
                    // abort 的请求不上报
                    if (xhr.__raven_xhr && xhr.__raven_xhr.abortedFlag) {
                        return;
                    }
                    // 是否需要上报
                    var needCapture = false;
                    var isSucc = xhr.status >= 200 && xhr.status <= 299;
                    var isRedirectAndCache = REDIRECT_CATCH_STATUS.indexOf(xhr.status) !== -1;
                    // 自动上报 API 异常，这里 readyState只处理4
                    // 状态码不为200~299且不为缓存状态码上报
                    if (!isSucc && !isRedirectAndCache) {
                        // 针对重定向和缓存，不做处理
                        needCapture = true;
                    }
                    // 状态码为200~299且isApiException用户自定义判断返回true上报
                    if (isSucc && isApiException && isFunction(isApiException)) {
                        var requestData = {
                            method: xhr.__raven_xhr.method,
                            url: xhr.responseURL,
                            status: xhr.status,
                            responseType: xhr.responseType,
                            response: res
                        };
                        needCapture = !!isApiException(requestData);
                    }
                    if (needCapture) {
                        var responseHeaders = '';
                        if (isFunction(xhr.getAllResponseHeaders)) {
                            responseHeaders = xhr.getAllResponseHeaders().trim().replace(/[\r\n]+/g, '***');
                        }
                        // 这里进行上报
                        client.captureAPIException({
                            url: xhr.responseURL || xhr.__raven_xhr.url,
                            method: xhr.__raven_xhr.method,
                            params: (sendBody && sendBody[0]) ? sendBody[0] : '',
                            status: xhr.status,
                            response: (isString(res) ? res : JSON.stringify(res)).slice(0, MAX_RES_LENGTH),
                            responseHeaders: responseHeaders,
                            user: '',
                            extra: 'AUTO_XHR' // 标识是自动上报的 API 异常
                        });
                    }
                }
                var props = ['onload', 'onerror', 'onprogress'];
                for (var j = 0; j < props.length; j++) {
                    wrapProp(props[j], xhr);
                }
                // 如果定义了 xhr 处理函数，hack 该函数
                if ('onreadystatechange' in xhr && isFunction(xhr.onreadystatechange)) {
                    fill(xhr, 'onreadystatechange', function (orig) {
                        return wrap({
                            mechanism: {
                                type: 'instrument',
                                data: {
                                    function: 'onreadystatechange',
                                    handler: (orig && orig.name) || '<anonymous>'
                                }
                            }
                        }, orig, onreadystatechangeHandler);
                    } /* intentionally don't track this instrumentation */);
                }
                else {
                    // if onreadystatechange wasn't actually set by the page on this xhr, we
                    // are free to set our own and capture the breadcrumb
                    xhr.onreadystatechange = onreadystatechangeHandler;
                }
                return origSend.apply(this, arguments);
            };
        }, client._wrappedBuiltIns);
    };
    /**
     * 监听 Fetch 网络请求事件
     */
    BehaviorPlugin.prototype.instrumentFetch = function () {
        if (!supportsFetch()) {
            return;
        }
        var _window = getGlobal();
        var client = this.client;
        var _a = client.options, collectAutoAPIException = _a.collectAutoAPIException, isApiException = _a.isApiException;
        var globalKey = client._globalKey;
        fill(global, 'fetch', function (origFetch) {
            return function () {
                // preserve arity
                // Make a copy of the arguments to prevent deoptimization
                // https://github.com/petkaantonov/bluebird/wiki/Optimization-killers#32-leaking-arguments
                var args = new Array(arguments.length);
                for (var i = 0; i < args.length; ++i) {
                    args[i] = arguments[i];
                }
                var fetchInput = args[0];
                var method = 'GET';
                var url;
                if (typeof fetchInput === 'string') {
                    url = fetchInput;
                }
                else if ('Request' in _window && fetchInput instanceof _window.Request) {
                    url = fetchInput.url;
                    if (fetchInput === null || fetchInput === void 0 ? void 0 : fetchInput.method) {
                        method = fetchInput.method;
                    }
                }
                else {
                    url = '' + fetchInput;
                }
                // if Sentry key appears in URL, don't capture, as it's our own request
                if (url.indexOf(globalKey) !== -1) {
                    return origFetch.apply(this, args);
                }
                if (args[1] && args[1].method) {
                    method = args[1].method;
                }
                var fetchData = {
                    method: method,
                    url: url,
                    status_code: null
                };
                return origFetch
                    .apply(this, args)
                    .then(function (response) {
                    var e_2, _a;
                    fetchData.status_code = response.status;
                    var paramsBody = '';
                    if (args[1] && args[1].body) {
                        paramsBody = args[1].body;
                    }
                    // 是否能复制fetch的response，根据结果分两种处理
                    var responseBehavior = isFunction(response.clone) ? response.clone() : null;
                    var behaviorParams = {
                        type: 'http',
                        category: 'fetch',
                        data: __assign(__assign({}, fetchData), { params: formatBehaviorData(paramsBody), response: '' }) // method, url, status_code
                    };
                    if (responseBehavior) {
                        responseBehavior.text().then(function (text) {
                            var res;
                            try {
                                res = JSON.parse(text);
                            }
                            catch (err) {
                                res = text;
                            }
                            behaviorParams.data.response = formatBehaviorData(res);
                            // 捕获 fetch 行为
                            client.captureBehavior(behaviorParams);
                        });
                    }
                    else {
                        // 捕获 fetch 行为
                        client.captureBehavior(behaviorParams);
                    }
                    // 是否自动上报
                    if (!collectAutoAPIException) {
                        return response;
                    }
                    // 是否需要上报判断
                    var needCapture = false;
                    // 是否能复制fetch的response，根据结果分两种处理
                    var responseCopy = isFunction(response.clone) ? response.clone() : null;
                    // 根据状态码是否是重定向
                    var isRedirectAndCache = REDIRECT_CATCH_STATUS.includes(response.status);
                    // 请求失败且不为重定向
                    if (!response.ok && !isRedirectAndCache) {
                        needCapture = true;
                    }
                    var requestData = {
                        method: fetchData.method,
                        url: fetchData.url,
                        status: response.status,
                        response: ''
                    };
                    // 这里进行上报
                    var responseHeaders = [];
                    try {
                        for (var _b = __values(response.headers.entries()), _c = _b.next(); !_c.done; _c = _b.next()) {
                            var pair = _c.value;
                            responseHeaders.push(pair[0] + ": " + pair[1]);
                        }
                    }
                    catch (e_2_1) { e_2 = { error: e_2_1 }; }
                    finally {
                        try {
                            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                        }
                        finally { if (e_2) throw e_2.error; }
                    }
                    // 异常参数
                    var errorParams = __assign(__assign({}, requestData), { params: paramsBody, responseHeaders: responseHeaders.join('***'), user: '', extra: 'AUTO_FETCH' // 标识是自动上报的 API 异常
                     });
                    if (responseCopy) {
                        // 都使用text()，只处理text和json格式
                        responseCopy.text().then(function (text) {
                            var res;
                            try {
                                res = JSON.parse(text);
                            }
                            catch (err) {
                                res = text;
                            }
                            if (response.ok && isFunction(isApiException)) {
                                needCapture = !!isApiException(__assign(__assign({}, requestData), { response: res }));
                            }
                            if (needCapture) {
                                client.captureAPIException(__assign(__assign({}, errorParams), { response: text.slice(0, MAX_RES_LENGTH) }));
                            }
                        }).catch(function (e) {
                            console.error(e);
                        });
                    }
                    else {
                        // 无法复制fetch response时
                        // 自动上报 API 异常
                        if (response.ok && isFunction(isApiException)) {
                            needCapture = !!isApiException(requestData);
                        }
                        if (needCapture) {
                            // 这里进行上报
                            client.captureAPIException(errorParams);
                        }
                    }
                    return response;
                })['catch'](function (err) {
                    // if there is an error performing the request
                    client.captureBehavior({
                        type: 'http',
                        category: 'fetch',
                        data: fetchData,
                        level: 'error'
                    });
                    throw err;
                });
            };
        }, client._wrappedBuiltIns);
    };
    /**
     * 监听路由变更事件
     */
    BehaviorPlugin.prototype.instrumentHistory = function () {
        var client = this.client;
        if (!supportsHistory()) {
            return;
        }
        var captureUrlChange = function (from, to) {
            var parsedLoc = parseUrl(global.location.href);
            var parsedTo = parseUrl(to);
            var parsedFrom = parseUrl(from);
            // Initial pushState doesn't provide `from` information
            if (!parsedFrom.path) {
                parsedFrom = parsedLoc;
            }
            // because onpopstate only tells you the "new" (to) value of location.href, and
            // not the previous (from) value, we need to track the value of the current URL
            // state ourselves
            lastHref = to;
            // Use only the path component of the URL if the URL matches the current
            // document (almost all the time when using pushState)
            if (parsedLoc.protocol === parsedTo.protocol && parsedLoc.host === parsedTo.host) {
                // tslint:disable-next-line:no-parameter-reassignment
                to = parsedTo.relative;
            }
            if (parsedLoc.protocol === parsedFrom.protocol && parsedLoc.host === parsedFrom.host) {
                // tslint:disable-next-line:no-parameter-reassignment
                from = parsedFrom.relative;
            }
            client.captureBehavior({
                category: 'navigation',
                data: {
                    from: from,
                    to: to
                }
            });
        };
        // record navigation (URL) changes
        var oldOnPopState = global.onpopstate;
        global.onpopstate = function (args) {
            var currentHref = global.location.href;
            captureUrlChange(lastHref, currentHref);
            if (oldOnPopState) {
                return oldOnPopState.apply(global, args);
            }
        };
        /** JSDoc */
        function historyReplacementFunction(originalHistoryFunction) {
            // note history.pushState.length is 0; intentionally not declaring
            // params to preserve 0 arity
            return function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var url = args.length > 2 ? args[2] : undefined;
                // url argument is optional
                if (url) {
                    // coerce to string (this is what pushState does)
                    captureUrlChange(lastHref, String(url));
                }
                return originalHistoryFunction.apply(this, args);
            };
        }
        fill(global.history, 'pushState', historyReplacementFunction);
        fill(global.history, 'replaceState', historyReplacementFunction);
    };
    /**
     * 监听 Console 相关方法
     */
    BehaviorPlugin.prototype.instrumentConsole = function () {
        // TODO: TO BE ADDED
    };
    /**
     * 监听 Beacon 相关方法
     */
    BehaviorPlugin.prototype.instrumentBeacon = function () {
        // TODO: TO BE ADDED
    };
    BehaviorPlugin.id = 'behaviors';
    return BehaviorPlugin;
}());
export { BehaviorPlugin };
//# sourceMappingURL=Behavior.js.map